/* eslint-disable prefer-template */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { IoChevronBack } from 'react-icons/io5';
import { BiLink } from 'react-icons/bi';

import {
  getCompanyAction,
  getDrugsAction,
  getTherapeuticAreasAction,
} from '../../actions/contentActions';
import { getProjectsAction } from '../../actions/projectsActions';
import { companyTypesOptions } from '../../constants/constants';
import TagsList from '../../components/TagsList/TagsList';
import styles from './CompanyView.module.scss';

function toId(value) {
  if (!value) return null;
  if (typeof value === 'object') return value._id || null;
  return value;
}

export default function CompanyView() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const company = useSelector((state) => state.content.company);
  const therapeuticAreasList = useSelector((state) => state.content.therapeuticAreas) || [];
  const drugsList = useSelector((state) => state.content.drugs) || [];
  const projectsList = useSelector((state) => state.projects.projectsList) || [];

  const companyAreaIds = useMemo(
    () => (company?.therapeuticAreas || []).map(toId).filter(Boolean),
    [company?.therapeuticAreas],
  );

  const companyProjects = useMemo(
    () => projectsList.filter((p) => toId(p?.company) === id),
    [projectsList, id],
  );

  const companyDrugs = useMemo(
    () => drugsList.filter((d) => toId(d?.company) === id),
    [drugsList, id],
  );

  const [filters, setFilters] = useState({
    therapeuticAreas: [],
    drugs: [],
  });
  const [filteredData, setFilteredData] = useState({
    therapeuticAreas: [],
    drugs: [],
    projects: [],
    studies: [],
  });

  const marketingProjects = filteredData?.projects?.filter((p) => p.type === 'marketing') || [];
  const medicalProjects = filteredData?.projects?.filter((p) => p.type === 'medical') || [];

  const typeLabel = companyTypesOptions.find((t) => t.value === company?.type)?.label
    || company?.type
    || '—';

  const load = useCallback(() => {
    if (!id) return;
    getCompanyAction(dispatch, id);
    getDrugsAction(dispatch);
    getTherapeuticAreasAction(dispatch);
    getProjectsAction(dispatch);
  }, [dispatch, id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!company?._id) return;

    const data = {
      therapeuticAreas: [],
      drugs: [],
      projects: [],
      studies: [...(company.studies || [])],
    };

    if (filters.drugs.length > 0) {
      data.drugs = filters.drugs;
    } else {
      data.drugs = companyDrugs.map((d) => d._id);
    }

    if (filters.therapeuticAreas.length > 0) {
      data.therapeuticAreas = filters.therapeuticAreas;
      data.drugs = companyDrugs
        .filter((d) => {
          const areas = (d.therapeuticAreas || []).map(toId);
          return filters.therapeuticAreas.some((t) => areas.includes(t));
        })
        .map((d) => d._id);
    } else {
      data.therapeuticAreas = companyAreaIds;
    }

    data.projects = companyProjects.filter((p) => {
      const pDrugs = (p.drugs || []).map(toId);
      return pDrugs.some((d) => data.drugs.includes(d));
    });

    data.studies = data.studies.filter((s) => {
      const sDrugs = (s.drugs || []).map((d) => toId(d?.drug || d));
      return sDrugs.some((d) => data.drugs.includes(d));
    });

    setFilteredData(data);
  }, [
    company,
    companyAreaIds,
    companyDrugs,
    companyProjects,
    filters.drugs,
    filters.therapeuticAreas,
  ]);

  const clinoctaBase = process.env.REACT_APP_CLINOCTA_URL;

  return (
    <div className={styles.container}>
      <header>
        <div className={styles.title}>
          <h2>
            {company?.name
              ? `CONCURRENTS / ${company.name}`
              : 'Concurrent'}
          </h2>
          <Link to="/majee/concurrents" className={styles.back}>
            <IoChevronBack size={20} />
            retour à la liste
          </Link>
        </div>
      </header>

      {company?._id && (
        <main className={styles.content}>
          <div className={styles.company}>
            <h1>{company?.name}</h1>
            <div className={`${styles.infos} ${styles.border}`}>
              <div className={styles.col}>
                <label>Type</label>
                <p>{typeLabel}</p>
              </div>
            </div>

            <section className={styles.border}>
              <TagsList
                label="Aire thérapeutique"
                options={therapeuticAreasList.map((t) => ({ value: t._id, label: t.name }))}
                selected={filteredData?.therapeuticAreas}
                isEdit={false}
              />
            </section>

            <section className={styles.border}>
              {drugsList?.length > 0 && (
                <TagsList
                  label="Produits"
                  options={drugsList.map((t) => ({ value: t._id, label: t.name }))}
                  selected={filteredData?.drugs}
                  isEdit={false}
                />
              )}
            </section>

            {filteredData?.studies?.length > 0 && (
              <section className={styles.border}>
                <label>Études cliniques</label>
                <div className={`${styles.list} ${styles.links}`}>
                  {filteredData.studies.map((p) => {
                    if (clinoctaBase) {
                      return (
                        <a
                          key={p._id}
                          href={`${clinoctaBase}/edit-study/${p._id}`}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.link}
                        >
                          <span>{p.name}</span>
                          <BiLink size={18} />
                        </a>
                      );
                    }
                    return (
                      <span key={p._id} className={styles.item}>{p.name}</span>
                    );
                  })}
                </div>
              </section>
            )}

            {marketingProjects.length > 0 && (
              <section className={styles.border}>
                <label>Projets marketing</label>
                <div className={styles.list}>
                  {marketingProjects.map((p) => (
                    <Link
                      key={p._id}
                      to={`/majee/project?project=${p._id}`}
                      className={styles.item}
                    >
                      <span>{p.name}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {medicalProjects.length > 0 && (
              <section className={styles.border}>
                <label>Projets médicaux</label>
                <div className={styles.list}>
                  {medicalProjects.map((p) => (
                    <Link
                      key={p._id}
                      to={`/majee/project?project=${p._id}`}
                      className={styles.item}
                    >
                      <span>{p.name}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <section className={styles.border}>
              <label>Partie RH</label>
              {company?.team?.rrh?.number > 0 && (
                <div className={styles['team-container']}>
                  <div className={styles['team-name']}>
                    <p>Equipe RRH</p>
                    <p>{company.team.rrh.number}</p>
                  </div>
                  {company.team.rrh.members?.length > 0 && (
                    <div className={styles.members}>
                      {company.team.rrh.members.map((m) => <p key={m}>{m}</p>)}
                    </div>
                  )}
                </div>
              )}
              {company?.team?.msl?.number > 0 && (
                <div className={styles['team-container']}>
                  <div className={styles['team-name']}>
                    <p>Equipe MSL</p>
                    <p>{company.team.msl.number}</p>
                  </div>
                  {company.team.msl.members?.length > 0 && (
                    <div className={styles.members}>
                      {company.team.msl.members.map((m) => <p key={m}>{m}</p>)}
                    </div>
                  )}
                </div>
              )}
              {company?.team?.kam?.number > 0 && (
                <div className={styles['team-container']}>
                  <div className={styles['team-name']}>
                    <p>Equipe kam</p>
                    <p>{company.team.kam.number}</p>
                  </div>
                  {company.team.kam.members?.length > 0 && (
                    <div className={styles.members}>
                      {company.team.kam.members.map((m) => <p key={m}>{m}</p>)}
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>

          <aside>
            <div className={styles.filters}>
              <label>Aire thérapeutiques</label>
              <div className={styles.tags}>
                <TagsList
                  options={therapeuticAreasList
                    .filter((t) => companyAreaIds.includes(t._id))
                    .map((t) => ({ value: t._id, label: t.name }))}
                  selected={filters.therapeuticAreas}
                  isFilter
                  handleChange={(array) => setFilters({ ...filters, therapeuticAreas: array })}
                  isEdit
                />
              </div>
              <div className={styles.tags}>
                <label>Produits</label>
                <TagsList
                  options={companyDrugs.map((d) => ({ value: d._id, label: d.name }))}
                  selected={filters.drugs}
                  isFilter
                  handleChange={(array) => setFilters({ ...filters, drugs: array })}
                  isEdit
                />
              </div>
            </div>
            <div className={styles.counter}>
              <label>Nombre de projets marketing</label>
              <p>{marketingProjects.length}</p>
            </div>
            <div className={styles.counter}>
              <label>Nombre de projets médicaux</label>
              <p>{medicalProjects.length}</p>
            </div>
          </aside>
        </main>
      )}
    </div>
  );
}
